import { get, post } from 'app/shared/utils/fetchr';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { CASE_QUERY_KEYS } from 'app/modules/cases/queries/keys';
import {
  InvestigationsCaseResponse,
  InvestigationsPayload,
} from 'app/modules/investigations/types';
import { useSelector } from 'react-redux';
import { selectHasReadCasesPermissions } from 'app/modules/session/selectors';
import { selectDLEnabledForCaseDetail } from 'app/shared/featureFlags/selectors';
import { useEffect, useMemo, useRef } from 'react';
import {
  COLD_STORAGE_MAX_RETRIES,
  getColdStorageRetryWaitTime,
  getLoadingFromColdStorageState,
  hasAnyCGDOsLoading,
  isArticleLoadingResponse,
} from 'app/shared/utils/fastArchHelpers';
import { CachedInvestigationsCaseResponse } from 'app/modules/investigations/types/responses';

const isLoadingResponse = (
  data: InvestigationsCaseResponse<false> | CachedInvestigationsCaseResponse,
): data is CachedInvestigationsCaseResponse => {
  return isArticleLoadingResponse(data, 'CASE');
};

export const useGetCase = (
  id: number,
  options: Pick<
    UseQueryOptions<InvestigationsCaseResponse<false>>,
    'enabled'
  > = {},
) => {
  const useNewEndpoint = useSelector(selectDLEnabledForCaseDetail);
  const dataUpdateCountRef = useRef(0);

  const { enabled = true } = options;
  const hasReadCasesPermission = useSelector(selectHasReadCasesPermissions);
  const query = useQuery({
    staleTime: 1000, // Prevent refetching on the first load
    enabled: enabled && hasReadCasesPermission,
    queryKey: CASE_QUERY_KEYS.getCase(id, useNewEndpoint),
    queryFn: () => {
      if (useNewEndpoint) {
        return get<CachedInvestigationsCaseResponse<false>>(
          `/v1/cases/cached/${id}`,
        );
      }
      return post<InvestigationsCaseResponse<false>>(
        '/v1/investigations/retrieve/case',
        {
          short: false,
          object_type: 'CASE',
          object_ids: [id],
        } satisfies InvestigationsPayload,
      );
    },
    refetchInterval: ({ state: { data } }) => {
      if (useNewEndpoint) {
        if (
          data &&
          isLoadingResponse(data) &&
          hasAnyCGDOsLoading(data.data_loader.article_ids[id]) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
        }
      }
      // Do not retry by default
      return false;
    },
  });

  const coldStorageLoading = useMemo(() => {
    return getLoadingFromColdStorageState(
      id,
      query.data && isLoadingResponse(query.data)
        ? query.data.data_loader
        : undefined,
    );
  }, [id, query.data]);

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [id]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  return {
    query,
    case: query.data?.cases[0],
    coldStorageLoading,
  };
};
